import { Col, Container, Row, Table } from "react-bootstrap";
import { useFetch } from "../../hooks/api";
import { GrStatusGoodSmall, GrStatusDisabledSmall } from "react-icons/gr";
import { Fragment } from "react";
import Accordion from "../../components/core/basic/Accordion";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../redux/hooks";
import { keepPreviousData } from "@tanstack/react-query";

interface FawcettItem {
  id: number;
  channel_id: number;
  enabled: boolean;
  links: {
    inventory: string;
    capabilities: string;
  };
}

interface ReportItem {
  report_id: number;
  status: string;
  created: string;
  report_type: string;
}

interface ReportCardResponse {
  org: { id: number; name: string; org_type: string; profile_type: string; status: string; days_active: number };
  anomalies: { name: string; status: string; anomalies?: { message: string } }[];
  reports: {
    pending: ReportItem[];
    iffy: ReportItem[];
  };
  fawcett: Record<string, FawcettItem[]>;
}

interface AnomaliesProps {
  anomalies: ReportCardResponse["anomalies"];
}

const Anomalies = ({ anomalies }: AnomaliesProps) => {
  return (
    <>
      {anomalies.length === 0 ?
        <div className={"alert alert-info"}>No anomalies found within the last 14 days.</div>
      : <Table striped bordered hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {anomalies.map((item, index) => {
              return (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{item.status}</td>
                  <td>{item.anomalies?.message}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      }
    </>
  );
};

interface FawcettProps {
  fawcett: ReportCardResponse["fawcett"];
}

const Fawcett = ({ fawcett }: FawcettProps) => {
  return (
    <>
      {Object.keys(fawcett).length === 0 ?
        <div className={"alert alert-info"}>No Fawcett data found</div>
      : <Table striped bordered hover>
          <thead>
            <tr>
              <th></th>
              <th>Channel</th>
              <th>ID</th>
              <th>Channel ID</th>
              <th>Links</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(fawcett).map((key) => {
              return fawcett[key]!.map((item, index) => {
                return (
                  <Fragment key={index}>
                    <tr>
                      <td className={"text-center"}>
                        {item.enabled ?
                          <GrStatusGoodSmall color="green" />
                        : <GrStatusDisabledSmall color="red" />}
                      </td>
                      <td className={"text-center"}>{key}</td>
                      <td className={"text-center"}>{item.id}</td>
                      <td className={"text-center"}>{item.channel_id}</td>
                      <td className={"text-end"}>
                        <ul className={"list-group list-group-horizontal-sm d-flex justify-content-end"}>
                          <a
                            href={`https://fawcett.growthvectors.io/admin/backend/credential/${item.id}`}
                            className={"list-group-item"}
                          >
                            Admin
                          </a>
                          <a href={item.links.inventory} className={"list-group-item"}>
                            Inventory
                          </a>
                          <a href={item.links.capabilities} className={"list-group-item"}>
                            Capabilities
                          </a>
                        </ul>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5}>
                        <Accordion title={"Details"} initialExpanded={false} marginTop={0}>
                          <pre>{JSON.stringify(item, null, 2)}</pre>
                        </Accordion>
                      </td>
                    </tr>
                  </Fragment>
                );
              });
            })}
          </tbody>
        </Table>
      }
    </>
  );
};

interface ImportReportTableProps {
  reports: ReportItem[];
}

const ImportReportTable = ({ reports }: ImportReportTableProps) => {
  return (
    <Table striped bordered hover className={"mt-4"}>
      <thead>
        <tr>
          <th>Report ID</th>
          <th>Status</th>
          <th>Created</th>
          <th>Type</th>
          <th>Links</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((item, index) => {
          return (
            <tr key={index}>
              <td>{item.report_id}</td>
              <td>{item.status}</td>
              <td>{item.created}</td>
              <td>{item.report_type}</td>
              <td className={"text-end"}>
                <ul className={"list-group list-group-horizontal-sm d-flex justify-content-end"}>
                  <a
                    href={`https://fawcett.growthvectors.io/api/reporting/warehouse/${item.report_id}`}
                    className={"list-group-item"}
                  >
                    Warehouse
                  </a>
                </ul>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const ReportCard = () => {
  const { org_id } = useParams<{ org_id: string }>();
  const { data, isFetching } = useFetch<ReportCardResponse>(
    ["reportcard", org_id],
    `/api/reportcard/${org_id}/`,
    {},
    {
      select: (response) => response.data,
      retry: 3,
      placeholderData: keepPreviousData,
    },
  );

  return (
    <Container
      style={{
        minHeight: "100%",
      }}
    >
      <Row>
        <Col>
          <h1 className={"fs-1"}>Organization Report Card</h1>
          <p>A summary of the currently active organization's internal state.</p>
          <ul className={"list-group list-group-horizontal-sm"}>
            <a href={"/user/staff/reportcard/"} className={"list-group-item"}>
              View System Report Card
            </a>
            {isFetching && (
              <li className={"list-group-item"}>
                <i className="fa fa-spinner fa-spin"></i> Updating...
              </li>
            )}
          </ul>
          <hr />
        </Col>
      </Row>
      {data && (
        <>
          <Row>
            <Col>
              <Table striped bordered hover>
                <tbody>
                  <tr>
                    <td>ID</td>
                    <td>{data.org.id}</td>
                  </tr>
                  <tr>
                    <td>Name</td>
                    <td>{data.org.name}</td>
                  </tr>
                  <tr>
                    <td>Type</td>
                    <td>
                      {data.org.org_type}
                      &nbsp;({data.org.profile_type})
                    </td>
                  </tr>
                  <tr>
                    <td>Status</td>
                    <td>
                      {data.org.status}
                      &nbsp;({data.org.days_active} days)
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={"Anomalies"} initialExpanded={true}>
                <Anomalies anomalies={data.anomalies} />
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={"Fawcett"} initialExpanded={true}>
                <Fawcett fawcett={data.fawcett} />
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={"Reports Awaiting Import"} initialExpanded={true}>
                {data.reports.pending.length === 0 ?
                  <div className={"alert alert-info"}>No pending reports found within the last 14 days.</div>
                : <ImportReportTable reports={data.reports.pending} />}
              </Accordion>
            </Col>
          </Row>
          <Row>
            <Col>
              <Accordion title={"Recently Failed Report Processing"} initialExpanded={true}>
                {data.reports.iffy.length === 0 ?
                  <div className={"alert alert-info"}>No failed reports found within the last 14 days.</div>
                : <ImportReportTable reports={data.reports.iffy} />}
              </Accordion>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

interface ReportCardOverviewResponse {
  active_anomalies: { name: string; anomalies: { org_id: number; org_name: string; value: number }[] }[];
}

const ReportCardOverview = () => {
  const { data, isFetching } = useFetch<ReportCardOverviewResponse>(
    ["reportcard"],
    "/api/reportcard/",
    {},
    {
      select: (response) => response.data,
      retry: 3,
      placeholderData: keepPreviousData,
    },
  );

  const user = useAppSelector((state) => state.user);

  return (
    <Container
      style={{
        minHeight: "100%",
      }}
    >
      <Row>
        <Col>
          <h1 className={"fs-1"}>System Report Card</h1>
          <ul className={"list-group list-group-horizontal-sm"}>
            <a href={`/user/staff/reportcard/${user.organization_id}`} className={"list-group-item"}>
              View Current Organization
            </a>
            {isFetching && (
              <li className={"list-group-item"}>
                <i className="fa fa-spinner fa-spin"></i> Updating...
              </li>
            )}
          </ul>
          <hr />
        </Col>
      </Row>
      {data && (
        <Row>
          <Col>
            <h2 className={"fs-2"}>Organizations With Active Anomalies</h2>
            {data.active_anomalies.map((group, index) => {
              return (
                <Fragment key={index}>
                  <h3 className={"fs-3"}>
                    <strong>{group.name}</strong> - anomaly
                  </h3>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Anomalies</th>
                        <th>Organization Name</th>
                        <th>Organization ID</th>
                        <th>Links</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.anomalies
                        .sort((a, b) => b.value - a.value)
                        .map((item, index) => {
                          return (
                            <tr key={index}>
                              <td className={"text-center"}>{item.value}</td>
                              <td className={"text-center"}>{item.org_name}</td>
                              <td className={"text-center"}>{item.org_id}</td>
                              <td className={"text-end"}>
                                <ul className={"list-group list-group-horizontal-sm d-flex justify-content-end"}>
                                  <a href={`/user/staff/reportcard/${item.org_id}/`} className={"list-group-item"}>
                                    Report Card
                                  </a>
                                </ul>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </Fragment>
              );
            })}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export { ReportCardOverview, ReportCard };
